import { Link } from 'react-router-dom';
import { Trash2, ShoppingCart, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { useCart } from '../contexts/CartContext';

const formatPrice = (p) => new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0 }).format(p);

export default function CartPage() {
  const { items, removeItem, clearCart, total } = useCart();

  if (items.length === 0) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 text-center animate-fade-in" data-testid="empty-cart">
        <ShoppingCart className="w-16 h-16 mx-auto text-[#e5dfd3] mb-4" />
        <h1 className="font-heading text-3xl font-bold text-[#46371c] mb-3">Tu carrito esta vacio</h1>
        <p className="font-body text-[#75664d] mb-6">Explora nuestros productos y encuentra la torta perfecta</p>
        <Link to="/productos">
          <Button className="bg-[#46371c] text-[#fcfbf9] hover:bg-[#2d2312] rounded-full px-8 font-body" data-testid="browse-products-btn">
            Ver productos
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 animate-fade-in" data-testid="cart-page">
      <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[#46371c] mb-8">Tu Carrito</h1>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-4">
          {items.map(item => (
            <Card key={item.cartId} className="p-4 border border-[#e5dfd3] rounded-2xl bg-[#fcfbf9]" data-testid={`cart-item-${item.cartId}`}>
              <div className="flex gap-4">
                <img src={item.image} alt={item.name} className="w-20 h-20 sm:w-24 sm:h-24 rounded-xl object-cover shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="font-heading text-lg font-semibold text-[#46371c]">{item.name}</h3>
                    <button onClick={() => removeItem(item.cartId)} className="p-1.5 text-[#75664d] hover:text-red-500 transition-colors shrink-0" data-testid={`remove-item-${item.cartId}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="font-body text-xs text-[#75664d] space-y-0.5 mt-1">
                    {item.size && <p>Tamano: {item.size.name}</p>}
                    {item.filling && <p>Relleno: {item.filling.name}</p>}
                    {item.flavor && <p>Sabor: {item.flavor.name}</p>}
                    {item.complements?.length > 0 && <p>Adiciones: {item.complements.map(c => `${c.name}${c.quantity > 1 ? ` (x${c.quantity})` : ''}`).join(', ')}</p>}
                  </div>
                  <p className="font-body text-base font-semibold text-[#46371c] mt-2">{formatPrice(item.subtotal)}</p>
                </div>
              </div>
            </Card>
          ))}
          <button onClick={clearCart} className="font-body text-sm text-[#75664d] hover:text-red-500 transition-colors" data-testid="clear-cart-btn">
            Vaciar carrito
          </button>
        </div>

        <div>
          <Card className="p-6 border border-[#e5dfd3] rounded-2xl bg-[#eef6f2] sticky top-24" data-testid="cart-summary">
            <h3 className="font-heading text-xl font-semibold text-[#46371c] mb-4">Resumen</h3>
            
            {/* Promo Alert */}
            {total >= 50000 && (
              <div className="bg-[#bce3cd]/50 border border-[#a0d5ba] text-[#347a54] rounded-xl p-3 mb-4 text-xs font-body animate-pulse" data-testid="promo-alert">
                🎉 <span className="font-bold">¡Regalo Gratis activado!</span> Por superar $50.000, te incluimos una <span className="font-bold">Vela Volcán</span> y un <span className="font-bold">Letrero de Feliz Cumpleaños</span> sin costo adicional.
              </div>
            )}

            <div className="space-y-2 font-body text-sm mb-4">
              <div className="flex justify-between text-[#75664d]"><span>Productos ({items.length})</span><span>{formatPrice(total)}</span></div>
              {total >= 50000 && (
                <div className="flex justify-between text-[#347a54] font-medium text-xs">
                  <span>Vela Volcán + Letrero</span>
                  <span className="line-through text-gray-400 mr-1">$17.000</span>
                  <span className="font-bold">¡Gratis!</span>
                </div>
              )}
              <div className="border-t border-[#e5dfd3] my-3" />
              <div className="flex justify-between text-[#46371c] font-semibold text-base"><span>Total</span><span>{formatPrice(total)}</span></div>
              <div className="flex justify-between text-[#a0d5ba] font-medium"><span>Deposito (50%)</span><span>{formatPrice(total * 0.5)}</span></div>
            </div>
            <Link to="/checkout">
              <Button className="w-full bg-[#46371c] text-[#fcfbf9] hover:bg-[#2d2312] rounded-full font-body font-medium" data-testid="checkout-btn">
                Ir al checkout <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <p className="font-body text-xs text-[#75664d] text-center mt-3">Deposito del 50% requerido para confirmar</p>
          </Card>
        </div>
      </div>
    </div>
  );
}
